import {Injectable, Injector} from "@angular/core";
import {BaseSupplementProvider} from "../tci-tenant-services";
import {
    ITCIApplication,
    ITCISupplement,
    ITCISupplementConnector,
    ITCITenantSupplementRequest,
    ITCITenantSupplementResponse,
    ITCIValidationResult,
    SERVICE_URLS,
    ITCIFile,
    TCITenantAppType
} from "@tci/common";
import {Observable, Subscription, Subject} from "rxjs";
import {Http} from "@angular/http";
import * as lodash from "lodash";

const ENCODING_STR = "Binary";
const BLOB_ENCODING_STR = "application/octet-stream";

@Injectable()
export class TciTenantBaseSupplementSharedService extends BaseSupplementProvider {

    private monitorSuppStatus: Subscription;
    private status$: Subject<ITCITenantSupplementResponse> = new Subject();

    constructor(private injector: Injector, private http: Http) {
        super(injector);
    }

    /**
     * Check before calling uploadSupplement
     * @param input
     */
    canCreate(input: ITCITenantSupplementRequest): Observable<boolean | ITCIValidationResult> {
        return undefined;
    }

    /**
     * Validate before uploading if there are content errors
     * The validation result can be an error or warning
     * @param input
     */
    validate(input: ITCITenantSupplementRequest): Observable<ITCIValidationResult> {
        return undefined;
    }

    /**
     * upload Supplement
     * @param input
     */
    createSupplement(input: ITCITenantSupplementRequest): Observable<ITCITenantSupplementResponse> {

        if (!lodash.isNull(input.appData) && !lodash.isEmpty(input.appData)) {
            const uploadFileObs =  Object.keys(input.appData).map(fileName => {
                // Supplement files upload to tmp api call.
                return this.uploadSuppFile(fileName, input.appData[fileName]);
            });

            return Observable.merge(...uploadFileObs)
                .toArray()
                .flatMap((fileUploadRes: ITCITenantSupplementResponse[]) => {
                    if (fileUploadRes.every(f => f.success)) {
                        // Supplement upload to AO api call
                        const url = `${SERVICE_URLS.SUPPLEMENT_UPLOAD}`;
                        return this.http.post(url, input).map(AOUploadRes => AOUploadRes.json())
                        .catch(err => {
                            console.error(`Failed to upload supplement: ${err}`);
                            return Observable.of(<ITCITenantSupplementResponse>{
                                success: false,
                                msg: `Failed to upload supplement : ${err}` // Goes to UI toastr
                            });
                        })
                        .flatMap((suppUploadRes: ITCITenantSupplementResponse) => {
                            if (suppUploadRes.success) {
                                this.startSuppMonitor(input.tenantType, input.connectorId);
                                return Observable.of(<ITCITenantSupplementResponse>{
                                    success: true,
                                    msg: suppUploadRes.msg
                                });
                            } else {
                                console.error("Failed to upload supplement : ", suppUploadRes);
                                // No polling to be done if err in "POST /supplement/upload" API.
                                return Observable.of(<ITCITenantSupplementResponse>suppUploadRes);
                            }
                        });
                    } else {
                        return Observable.of(<ITCITenantSupplementResponse>{
                            success: false,
                            msg: "Failed to upload supplement."
                        });
                    }
                });
        } else {
            return Observable.of(<ITCITenantSupplementResponse>{
                success: false,
                msg: `Failed to upload supplement.`,
                result: input
            });
        }
    }

    /* Upload files to tmp location. */
    private uploadSuppFile(uniqFileName: string, file: ITCIFile): Observable<ITCITenantSupplementResponse> {
         /* file.content holds the browser file object */
        const FILE_OBJ: File = file.content;
        const formData = new FormData;
        /* uniqFileName field is used in WS backend write stream as fileName. */
        formData.append(uniqFileName, FILE_OBJ, FILE_OBJ.name);

        const URL = `${SERVICE_URLS.SUPPLEMENT_FILE_UPLOAD}`;
        return this.http.post(URL, formData).map(res => res.json())
            .catch(err => {
                console.error(`Failed to upload file ${FILE_OBJ.name}`);
                return Observable.of(<ITCITenantSupplementResponse>{
                    success: false,
                    msg: "Failed to upload supplement."
                });
            });
    }

    /* Supplement upload status polling. */
    private getSuppStatus(appType: TCITenantAppType, connectorName: string): Observable<null> {
        const requestFrequency = 10000;
        return Observable.timer(5000, requestFrequency)
            .flatMap(() => {
                return this.http.get(`${SERVICE_URLS.SUPPLEMENT_UPLOAD_STATUS}/${appType.toString()}/${connectorName}`)
                    .map((uploadStatusRes) => {
                        const status: ITCITenantSupplementResponse = uploadStatusRes.json();
                        if (status && status.success) {
                            this.publishStatus(status);
                            this.stopSuppMonitor();
                        }
                        return null;
                    })
                    .catch(err => {
                        console.error(`Unable to get upload supplement status: ${err}`);
                        const respForUI = <ITCITenantSupplementResponse>{
                            success: false,
                            msg: `Failed to upload supplement.`, // Goes to UI toastr
                            result: err
                        };

                        try {
                            const errBody = <ITCITenantSupplementResponse>JSON.parse(err._body);
                            respForUI.msg = errBody.msg;
                            respForUI.result = errBody.result;
                        } catch (e) {
                            console.error("Error parsing supplement status error response : ", e);
                        }
                        this.publishStatus(respForUI);
                        this.stopSuppMonitor();
                        return Observable.of(null);
                    });
            });
    }

    getStatusSubject(): Subject<ITCITenantSupplementResponse> {
        return this.status$;
    }

    private publishStatus(suppRes: ITCITenantSupplementResponse) {
        this.status$.next(suppRes);
    }

    private startSuppMonitor(appType: TCITenantAppType, connName: string) {
        this.monitorSuppStatus = this.getSuppStatus(appType, connName).subscribe();
    }

    private stopSuppMonitor() {
        if (this.monitorSuppStatus) {
            this.monitorSuppStatus.unsubscribe();
        }
        delete this.monitorSuppStatus;
    }

    /**
     * Return a list of tenant specific supplements, the UI will use this to get supplements before
     * doing aggregation of the combined list, one from each tenant
     */
    getSupplements(): Observable<ITCISupplement[]> {
        const url = `${SERVICE_URLS.SUPPLEMENT_LIST}`;
        return this.http.get(url).map(res => res.json())
        .catch(err => {
            console.error(`Unable to get supplement list: ${err}`);
            return Observable.of([]);
        });
    }

    /**
     * Get List of Supplement Connectors
     * @param input
     */
    getAllSupplementConnectors(input: ITCITenantSupplementRequest): Observable<ITCISupplementConnector[]> {
        const url = `${SERVICE_URLS.SUPPLEMENT_CONNECTOR}/${input.tenantType.toString()}`;
        return this.http.get(url).map(res => res.json())
        .catch(err => {
            console.error(`Unable to get supplement connectors: ${err}`);
            return Observable.of([]);
        });
    }


    /**
     * Get List of Connected Applications
     * @param input
     */
    getConnectedAppsById(input: ITCITenantSupplementRequest): Observable<ITCIApplication[]> {
        return undefined;
    }

    /**
     * download Supplement will start downloading one or more files
     * @param input
     */
    downloadSupplement(input: ITCITenantSupplementRequest): Observable<ITCITenantSupplementResponse> {
        return undefined;
    }

    /**
     * Check before calling updateSupplement
     * @param input
     */
    canUpdate(input: ITCITenantSupplementRequest): Observable<boolean | ITCIValidationResult> {
        return undefined;
    }

    /**
     * update Supplement
     * @param input
     */
    updateSupplement(input: ITCITenantSupplementRequest): Observable<ITCITenantSupplementResponse> {
        return undefined;
    }

    /**
     * Check before calling deleteSupplement
     * @param input
     */
    canDelete(input: ITCITenantSupplementRequest): Observable<boolean | ITCIValidationResult> {
        return undefined;
    }

    /**
     * delete Supplement
     * @param input
     */
    deleteSupplement(input: ITCITenantSupplementRequest): Observable<ITCITenantSupplementResponse> {
        return undefined;
    }
    /**
     * @param input: @ITCITenantSupplementRequest
     * @param callback: (cinput: @ITCITenantSupplementRequest) => @Observable<@ITCITenantSupplementResponse>
     * @return @Observable<@ITCITenantSupplementResponse>
     */
    pushSuppChanges(input: ITCITenantSupplementRequest,
        cb: (cinput: ITCITenantSupplementRequest) =>
            Observable<ITCITenantSupplementResponse>): Observable<ITCITenantSupplementResponse> {
                return undefined;
    }

    /**
     * Check if supplement provider is busy.
     * @param input
     */
    isBusy(input: ITCITenantSupplementRequest): Observable<boolean> {
        return undefined;
    }


}
